import type { DataTableColumn } from '@odo/shared-ui';
import { PatientLink } from '../PatientLink';
import { PracticeNameCell } from '../PracticeNameCell';
import { fmtPoDate } from './poFormat';
import { ExpectedCost } from './ExpectedCost';
import { expectedCostSummary } from './poCost';
import { PoAttentionMarker } from './PoAttentionMarker';
import { PoStatusCell } from './PoStatusCell';
import type { PurchaseOrderListItem, PurchaseOrdersSortBy } from '../../../services/purchaseOrders';
import { formatMoneyDollars } from '../../../utils/money';

interface BuildPurchaseOrderColumnsArgs {
    showPracticeColumn: boolean;
}

// Always labelled - a bare date next to a pill reads as "due" or "paid"
// depending on the pill, which is exactly the confusion to avoid.
function billingMoment(po: PurchaseOrderListItem): string | null {
    if (po.paidAt) return `Paid ${fmtPoDate(po.paidAt)}`;
    if (po.vendorInvoiceDueDate) return `Due ${fmtPoDate(po.vendorInvoiceDueDate)}`;
    return null;
}

// shippedAt is the earliest live line to start shipping, never the whole order.
function shippingMoment(po: PurchaseOrderListItem): string | null {
    if (po.shippedAt) return `Shipped ${fmtPoDate(po.shippedAt)}`;
    return null;
}

// Invoice number first: the same number repeating down the column is how a
// vendor switching to consolidated invoicing shows up in the data.
function vendorInvoiceMeta(po: PurchaseOrderListItem): string | null {
    const parts = [
        po.vendorInvoiceNumber,
        po.vendorInvoiceDueDate ? `Due ${fmtPoDate(po.vendorInvoiceDueDate)}` : null
    ].filter((part): part is string => Boolean(part));
    return parts.length > 0 ? parts.join(' · ') : null;
}

// Widths are a ternary on the same flag throughout: hiding the practice
// column redistributes its share across the other columns.
export function buildPurchaseOrderColumns({
    showPracticeColumn
}: BuildPurchaseOrderColumnsArgs): DataTableColumn<PurchaseOrderListItem, PurchaseOrdersSortBy>[] {
    return [
        {
            key: 'poNumber',
            label: 'PO',
            width: showPracticeColumn ? '9%' : '10%',
            cellClassName: 'pl-6 pr-3 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3 pl-6',
            render: (po) => (
                <span className="flex items-center gap-1.5">
                    <span className="font-mono text-[12px] text-gray-700 truncate">
                        {po.poNumber}
                    </span>
                    <PoAttentionMarker purchaseOrder={po} />
                </span>
            )
        },
        {
            key: 'practice',
            label: 'Practice',
            width: '12%',
            visible: showPracticeColumn,
            truncate: true,
            cellClassName: 'px-3 text-sm',
            headerClassName: 'whitespace-nowrap px-3',
            getTitle: (po) => po.practiceName,
            render: (po) => (
                <PracticeNameCell
                    practiceName={po.practiceName}
                    practiceDoctorName={po.practiceDoctorName}
                />
            )
        },
        {
            key: 'patient',
            label: 'Patient',
            width: showPracticeColumn ? '10%' : '13%',
            truncate: true,
            cellClassName: 'px-3 text-sm text-gray-700',
            headerClassName: 'whitespace-nowrap px-3',
            getTitle: (po) => po.patientName ?? undefined,
            render: (po) => (
                <PatientLink patientId={po.patientId} patientName={po.patientName}>
                    {po.patientName ?? <span className="text-gray-300">-</span>}
                </PatientLink>
            )
        },
        {
            key: 'vendor',
            label: 'Vendor',
            width: showPracticeColumn ? '10%' : '12%',
            truncate: true,
            cellClassName: 'px-3 text-sm text-gray-700',
            headerClassName: 'whitespace-nowrap px-3',
            getTitle: (po) => po.vendor.name,
            render: (po) => po.vendor.name
        },
        {
            key: 'expected',
            label: 'Expected',
            width: showPracticeColumn ? '12%' : '14%',
            align: 'right',
            cellClassName: 'px-3',
            headerClassName: 'whitespace-nowrap px-3',
            render: (po) => <ExpectedCost summary={expectedCostSummary(po)} />
        },
        {
            key: 'invoiced',
            label: 'Vendor invoice',
            width: showPracticeColumn ? '12%' : '13%',
            sortable: true,
            sortKey: 'vendorInvoiceDueDate',
            align: 'right',
            cellClassName: 'px-3 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3',
            getTitle: (po) => vendorInvoiceMeta(po) ?? undefined,
            render: (po) => {
                const meta = vendorInvoiceMeta(po);
                if (po.vendorInvoicedAmount === null && meta === null) {
                    return <span className="text-sm text-gray-300">-</span>;
                }
                return (
                    <span className="flex flex-col items-end gap-0.5">
                        {po.vendorInvoicedAmount === null ? (
                            <span className="text-sm text-gray-300">-</span>
                        ) : (
                            <span className="text-sm font-semibold text-gray-900 tabular-nums">
                                {formatMoneyDollars(po.vendorInvoicedAmount)}
                            </span>
                        )}
                        {meta && (
                            <span className="w-full text-right text-[11px] text-gray-400 truncate">
                                {meta}
                            </span>
                        )}
                    </span>
                );
            }
        },
        {
            key: 'fulfillmentStatus',
            label: 'Shipping',
            width: showPracticeColumn ? '14%' : '15%',
            cellClassName: 'px-3',
            headerClassName: 'whitespace-nowrap px-3',
            getTitle: (po) => shippingMoment(po) ?? undefined,
            render: (po) => (
                <PoStatusCell
                    kind="fulfillment"
                    status={po.fulfillmentStatus}
                    moment={shippingMoment(po)}
                />
            )
        },
        {
            key: 'billingStatus',
            label: 'Billing',
            width: showPracticeColumn ? '13%' : '14%',
            cellClassName: 'px-3',
            headerClassName: 'whitespace-nowrap px-3',
            getTitle: (po) => billingMoment(po) ?? undefined,
            render: (po) => (
                <PoStatusCell kind="billing" status={po.billingStatus} moment={billingMoment(po)} />
            )
        },
        {
            key: 'createdAt',
            label: 'Created',
            width: showPracticeColumn ? '8%' : '9%',
            sortable: true,
            sortKey: 'createdAt',
            cellClassName: 'px-3 text-sm text-gray-500 whitespace-nowrap',
            headerClassName: 'whitespace-nowrap px-3',
            render: (po) => fmtPoDate(po.createdAt)
        }
    ];
}
